<script setup lang="ts">
import { ref, computed, onMounted } from 'vue'
import AppComponent from '@/views/base/AppComponent.vue'

interface Props {
  title: string
  subtitle: string
  stats: { value: string; label: string }[]
  csvUrl: string
  otherPageUrl: string
  otherPageLabel: string
  backUrl: string
  backLabel: string
  lastUpdated: string
}

const props = defineProps<Props>()

interface CoverageRow {
  country: string
  code: string
  section: string
  gdn: boolean
  search: boolean
  shopping: boolean
  youtube: boolean
  linkedin: boolean
  meta: boolean
  pinterest: boolean
  snapchat: boolean
  tiktok: boolean
  x: boolean
}

const PLATFORMS = [
  { key: 'gdn', label: 'GDN', icon: '/assets/platforms/google.png' },
  { key: 'search', label: 'SEARCH', icon: '/assets/platforms/google.png' },
  { key: 'shopping', label: 'SHOPPING', icon: '/assets/platforms/google.png' },
  { key: 'youtube', label: 'YOUTUBE', icon: '/assets/platforms/google.png' },
  { key: 'linkedin', label: 'LINKEDIN', icon: '/assets/platforms/linkedin.png' },
  { key: 'meta', label: 'META', icon: '/assets/platforms/meta.png' },
  { key: 'pinterest', label: 'PINTEREST', icon: '/assets/platforms/pinterest.png' },
  { key: 'snapchat', label: 'SNAPCHAT', icon: '/assets/platforms/snapchat.png' },
  { key: 'tiktok', label: 'TIKTOK', icon: '/assets/platforms/tiktok.png' },
  { key: 'x', label: 'X (Twitter)', icon: '/assets/platforms/x.png' },
]

const rows = ref<CoverageRow[]>([])
const query = ref('')
const activePlatform = ref<number | null>(null)
const loadError = ref<string | null>(null)

// Boolean columns in the coverage CSVs — one per platform key in PLATFORMS.
// Kept as a Set so parseCSV can (a) coerce values to boolean and (b) default
// missing columns to `false` (guards against stale CSV vs. new code — see PR
// #176 review, HelpGeoCoverageBase silent-undefined finding).
const BOOL_COLUMNS = new Set(PLATFORMS.map((p) => p.key))

function parseCSV(csv: string): CoverageRow[] {
  const lines = csv.trim().split('\n')
  if (lines.length < 2) return []
  const headers = lines[0].split(',').map((h) => h.trim().toLowerCase())
  const presentBoolCols = new Set(headers.filter((h) => BOOL_COLUMNS.has(h)))

  return lines.slice(1).map((line) => {
    const values = line.split(',')
    const row: Record<string, string | boolean> = {}

    // First, default every expected boolean column to `false` — so a stale
    // CSV missing e.g. `x` doesn't leak `undefined` into the template.
    for (const key of BOOL_COLUMNS) {
      row[key] = false
    }

    headers.forEach((h, i) => {
      const val = values[i]?.trim() || ''
      if (BOOL_COLUMNS.has(h)) {
        row[h] = val === 'true'
      } else {
        row[h] = val
      }
    })

    // Log once per unexpected shape to catch stale-CSV drift in dev.
    if (import.meta.env.DEV && presentBoolCols.size < BOOL_COLUMNS.size) {
      const missing = [...BOOL_COLUMNS].filter((k) => !presentBoolCols.has(k))

      console.warn(`[HelpGeoCoverage] CSV missing columns: ${missing.join(', ')}`)
    }

    return row as unknown as CoverageRow
  })
}

onMounted(async () => {
  try {
    const res = await fetch(props.csvUrl)
    if (!res.ok) throw new Error(`Failed: ${res.status}`)
    rows.value = parseCSV(await res.text())
  } catch {
    loadError.value = 'Failed to load coverage data'
  }
})

function flag(code: string): string {
  try {
    return String.fromCodePoint(
      ...[...code.toUpperCase()].map((c) => 0x1f1e6 + c.charCodeAt(0) - 65),
    )
  } catch {
    return ''
  }
}

const filteredRows = computed(() => {
  let result = rows.value
  const q = query.value.toLowerCase().trim()
  if (q) {
    result = result.filter((r) => r.country.toLowerCase().includes(q))
  }
  if (activePlatform.value !== null) {
    const pKey = PLATFORMS[activePlatform.value].key as keyof CoverageRow
    result = result.filter((r) => r[pKey] === true)
  }
  return result
})

const sections = computed(() => {
  const sectionMap: Record<string, CoverageRow[]> = {}
  for (const row of filteredRows.value) {
    if (!sectionMap[row.section]) sectionMap[row.section] = []
    sectionMap[row.section].push(row)
  }
  return Object.entries(sectionMap).map(([name, rows]) => ({ name, rows }))
})

const showingLabel = computed(() => {
  const count = filteredRows.value.length
  const suffix = count === 1 ? 'country' : 'countries'
  const platformLabel =
    activePlatform.value !== null
      ? ` on ${PLATFORMS[activePlatform.value].label.toLowerCase()}`
      : ''
  return `${count} ${suffix}${platformLabel}`
})

const hasFilters = computed(() => query.value.trim() !== '' || activePlatform.value !== null)

function clearFilters() {
  query.value = ''
  activePlatform.value = null
}

function togglePlatform(idx: number) {
  activePlatform.value = activePlatform.value === idx ? null : idx
}
</script>

<template>
  <AppComponent :title="title" id="help-geo-coverage">
    <div class="help-article">
      <RouterLink :to="backUrl" class="back-link">
        <UIcon name="i-lucide-arrow-left" class="size-4" />
        {{ backLabel }}
      </RouterLink>

      <h1 class="page-title">{{ title }}</h1>
      <p class="page-subtitle">{{ subtitle }}</p>
      <p class="page-meta">Last updated: {{ lastUpdated }}</p>

      <!-- STATS + LEGEND -->
      <div class="stats-row">
        <div class="stats">
          <div v-for="stat in stats" :key="stat.label" class="stat">
            <div class="stat-value">{{ stat.value }}</div>
            <div class="stat-label">{{ stat.label }}</div>
          </div>
        </div>
        <div class="legend">
          <span class="legend-item">
            <UIcon name="i-lucide-check" class="legend-check" />
            Covered
          </span>
          <span class="legend-item">
            <span class="legend-dot"></span>
            Not covered
          </span>
        </div>
      </div>

      <!-- CONTROL BAR -->
      <div class="controls">
        <UInput
          v-model="query"
          placeholder="Search countries…"
          icon="i-lucide-search"
          size="sm"
          class="search-input"
        />
        <UButton v-if="hasFilters" color="neutral" variant="ghost" size="sm" @click="clearFilters"
          >Clear</UButton
        >
        <div class="controls-spacer"></div>
        <span class="showing-label">Showing {{ showingLabel }}</span>
        <UButton color="neutral" variant="ghost" size="sm" :to="otherPageUrl">
          {{ otherPageLabel }}
        </UButton>
      </div>

      <!-- MATRIX -->
      <div class="matrix-wrap">
        <table class="matrix">
          <colgroup>
            <col style="width: 180px" />
            <col v-for="p in PLATFORMS" :key="p.key" style="width: 80px" />
          </colgroup>
          <thead>
            <tr>
              <th class="th-country">Country</th>
              <th
                v-for="(p, idx) in PLATFORMS"
                :key="p.key"
                :class="['th-platform', { active: activePlatform === idx }]"
                @click="togglePlatform(idx)"
              >
                <div class="th-content">
                  <img :src="p.icon" class="platform-icon" width="20" height="20" alt="" />
                  <span :class="['th-label', { active: activePlatform === idx }]">{{
                    p.label
                  }}</span>
                  <div :class="['th-bar', { active: activePlatform === idx }]"></div>
                </div>
              </th>
            </tr>
          </thead>
          <tbody>
            <template v-for="sec in sections" :key="sec.name">
              <tr class="section-row">
                <td :colspan="PLATFORMS.length + 1">
                  <div class="section-header">
                    <span class="section-name">
                      <span class="section-dot"></span>
                      {{ sec.name }}
                    </span>
                    <span class="section-count"
                      >{{ sec.rows.length }}
                      {{ sec.rows.length === 1 ? 'country' : 'countries' }}</span
                    >
                  </div>
                </td>
              </tr>
              <tr
                v-for="(row, idx) in sec.rows"
                :key="row.code"
                :class="['data-row', { alt: idx % 2 === 1 }]"
              >
                <td class="cell-country">
                  <span class="flag">{{ flag(row.code) }}</span>
                  {{ row.country }}
                </td>
                <td v-for="p in PLATFORMS" :key="p.key" class="cell-platform">
                  <UIcon
                    v-if="row[p.key as keyof CoverageRow]"
                    name="i-lucide-check"
                    class="check-icon"
                  />
                  <span v-else class="not-covered"></span>
                </td>
              </tr>
            </template>
            <tr v-if="filteredRows.length === 0">
              <td :colspan="PLATFORMS.length + 1" class="empty-message">
                No countries match your filters.
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      <!-- FOOTER NOTE -->
      <p class="footer-note">
        <strong>NB</strong> Coverage depends on advertiser identification and may evolve as
        platforms expand.
      </p>
    </div>
  </AppComponent>
</template>

<style scoped>
.help-article {
  --accent: #f59e0b;
  --accent-soft: color-mix(in srgb, #f59e0b 14%, transparent);
  max-width: 1200px;
  margin: 0 auto;
  padding: 48px;
}

.back-link {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  font-size: 13px;
  color: var(--ui-text-muted);
  margin-bottom: 32px;
  background: none;
  border: none;
  cursor: pointer;
  transition: color 0.15s;
}
.back-link:hover {
  color: var(--ui-text);
}

.page-title {
  font-size: 42px;
  font-weight: 700;
  letter-spacing: -0.02em;
  color: var(--ui-text-highlighted);
  margin: 0;
}

.page-subtitle {
  margin: 12px 0 0;
  font-size: 16px;
  line-height: 1.6;
  color: var(--ui-text-muted);
  max-width: 700px;
}

.page-meta {
  margin: 8px 0 0;
  font-size: 13px;
  color: var(--ui-text-dimmed);
}

/* Stats */
.stats-row {
  display: flex;
  align-items: flex-end;
  justify-content: space-between;
  gap: 24px;
  flex-wrap: wrap;
  margin-top: 32px;
  padding-top: 24px;
  border-top: 1px solid var(--ui-border);
}
.stats {
  display: flex;
  gap: 36px;
  flex-wrap: wrap;
}
.stat-value {
  font-size: 32px;
  font-weight: 700;
  line-height: 1;
  color: var(--ui-text-highlighted);
}
.stat-label {
  margin-top: 6px;
  font-size: 11px;
  letter-spacing: 0.1em;
  text-transform: uppercase;
  color: var(--ui-text-muted);
  font-weight: 500;
}
.legend {
  display: flex;
  align-items: center;
  gap: 18px;
}
.legend-item {
  display: inline-flex;
  align-items: center;
  gap: 7px;
  font-size: 12px;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: var(--ui-text-muted);
  font-weight: 500;
}
.legend-check {
  color: var(--accent);
  width: 14px;
  height: 14px;
}
.legend-dot {
  display: inline-block;
  width: 6px;
  height: 6px;
  border-radius: 9999px;
  background: var(--ui-border-accented);
}

/* Controls */
.controls {
  display: flex;
  align-items: center;
  gap: 14px;
  flex-wrap: wrap;
  margin-top: 24px;
  padding: 16px 0;
  border-bottom: 1px solid var(--ui-border);
}
.search-input {
  width: 240px;
}
.controls-spacer {
  flex: 1;
}
.showing-label {
  font-size: 13px;
  color: var(--ui-text-muted);
  font-variant-numeric: tabular-nums;
}

/* Matrix */
.matrix-wrap {
  overflow: auto;
  margin-top: 24px;
  border: 1px solid var(--ui-border);
  border-radius: 12px;
}
.matrix {
  width: 100%;
  min-width: 900px;
  border-collapse: collapse;
  table-layout: fixed;
  font-size: 14px;
}
.th-country {
  position: sticky;
  top: 0;
  z-index: 3;
  text-align: left;
  padding: 16px 12px 12px 20px;
  vertical-align: bottom;
  background: var(--ui-bg);
  border-bottom: 1px solid var(--ui-border);
  font-size: 11px;
  letter-spacing: 0.1em;
  text-transform: uppercase;
  color: var(--ui-text-muted);
  font-weight: 600;
  width: 180px;
}
.th-platform {
  position: sticky;
  top: 0;
  z-index: 2;
  padding: 14px 8px 10px;
  vertical-align: bottom;
  cursor: pointer;
  background: var(--ui-bg);
  border-bottom: 1px solid var(--ui-border);
  transition: background-color 0.12s ease;
  width: 80px;
  text-align: center;
}
.th-platform.active {
  background: var(--accent-soft);
}
.th-content {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 6px;
}
.platform-icon {
  color: var(--ui-text-muted);
}
.th-label {
  font-size: 10px;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  color: var(--ui-text-muted);
  font-weight: 500;
}
.th-label.active {
  color: var(--ui-text-highlighted);
  font-weight: 600;
}
.th-bar {
  height: 2px;
  width: 20px;
  border-radius: 2px;
  background: transparent;
}
.th-bar.active {
  background: var(--accent);
}

/* Section rows */
.section-row td {
  background: var(--ui-bg-inverted);
  padding: 10px 20px;
}
.section-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
}
.section-name {
  display: inline-flex;
  align-items: center;
  gap: 10px;
  font-size: 12px;
  letter-spacing: 0.12em;
  text-transform: uppercase;
  color: var(--ui-text-inverted);
  font-weight: 600;
}
.section-dot {
  width: 8px;
  height: 8px;
  background: var(--accent);
}
.section-count {
  font-size: 11px;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: var(--color-neutral-400);
  font-weight: 500;
}

/* Data rows */
.data-row {
  background: var(--ui-bg);
}
.data-row.alt {
  background: var(--ui-bg-muted);
}
.data-row:hover {
  background: var(--ui-bg-elevated);
}
.cell-country {
  text-align: left;
  padding: 10px 12px 10px 20px;
  border-bottom: 1px solid var(--ui-border-muted);
  white-space: nowrap;
  color: var(--ui-text);
  font-weight: 450;
  width: 180px;
}
.flag {
  margin-right: 10px;
}
.cell-platform {
  text-align: center;
  padding: 10px 6px;
  border-bottom: 1px solid var(--ui-border-muted);
  width: 80px;
}
.check-icon {
  display: block;
  margin: 0 auto;
  color: var(--accent);
  width: 14px;
  height: 14px;
}
.not-covered {
  display: inline-block;
  width: 6px;
  height: 6px;
  border-radius: 9999px;
  background: var(--ui-border-accented);
  vertical-align: middle;
}
.empty-message {
  padding: 48px 20px;
  text-align: center;
  color: var(--ui-text-muted);
  font-size: 14px;
}

/* Footer */
.footer-note {
  margin-top: 24px;
  font-size: 13px;
  color: var(--ui-text-muted);
}
.footer-note strong {
  color: var(--accent);
  font-weight: 600;
  letter-spacing: 0.04em;
}
</style>
